import styled from '@emotion/styled';
import { SpeakingSlotRegistrationCandidateDto, SpeakingSlotStatus, SpeakingSlotType } from 'api/planning';
import classNames from 'classnames';
import { Popup } from 'semantic-ui-react';
import { SpeakingSlotRegistrationBookingPopup } from '../registrations/SpeakingSlotRegistrationBookingPopup';
import { SwaSlotBookedPopup } from '../registrations/SwaSlotBookedPopup';

type Props = {
  cell: {
    status?: SpeakingSlotStatus;
    startMinutes: string;
    endMinutes: string;
    startTime: string;
    endTime: string;
    occupied: boolean;
    booking?: SpeakingSlotRegistrationCandidateDto;
    clashed: boolean;
    plannedCapacity?: number;
    availableCapacity?: number;
  };
  onMouseEnter: (startTime: string) => void;
  speakingSlotType: SpeakingSlotType;
};

export const SpeakingSlot = ({ cell, onMouseEnter, speakingSlotType }: Props) => {
  const occupiedSeats = (cell.plannedCapacity ?? 0) - (cell.availableCapacity ?? 0);
  const isAnySwaSeatBooked = speakingSlotType === SpeakingSlotType.SWA && occupiedSeats > 0;

  const cellClashedContent = (
    <S.ClashedCell
      onMouseEnter={() => onMouseEnter(cell.startTime)} />
  );

  const cellContent = (
    <S.Cell
      className={classNames(
        cell.status !== undefined ? SpeakingSlotStatus[cell.status] : null,
        SpeakingSlotType[speakingSlotType],
        {
          occupied: cell.occupied,
          clashed: cell.clashed,
          SwaBooked: isAnySwaSeatBooked
        }
      )}
      onMouseEnter={() => onMouseEnter(cell.startTime)}
    >
      <div>{cell.startMinutes}</div>
      <S.Divider />
      <div>{cell.endMinutes}</div>
    </S.Cell>
  );

  const isAlreadyPlanned = cell.status != undefined;
  if (cell.clashed && !isAlreadyPlanned) {
    return cellClashedContent;
  }

  let popupContent = null;
  if (isAnySwaSeatBooked) {
    popupContent = (
      <SwaSlotBookedPopup occupiedSeats={occupiedSeats}
        startTime={cell.startTime}
        endTime={cell.endTime}
      />
    );
  } else if (cell.booking) {
    popupContent = (
      <SpeakingSlotRegistrationBookingPopup booking={cell.booking}
        startTime={cell.startTime}
        endTime={cell.endTime}
      />
    );
  }

  return popupContent ? (
    <Popup
      position='top center'
      content={popupContent}
      trigger={cellContent}
    />
  ) : (
    cellContent
  );
};

const S = {
  ClashedCell: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    background-color: #d5d8dd;
    border-color: #d5d8dd;

    .action-AddSlot &,
    .action-AddBreak &,
    .action-Clear & {    
      cursor: not-allowed;
    }
  `,
  Cell: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-left: 1px solid #e2e2e3;
    border-top: 1px solid #e2e2e3;
    border-bottom: 1px solid #e2e2e3;
    background-color: #fff;
    color: #b3b3b3;
    line-height: 1.1rem;
    font-size: 12px;
    &.Available,
    &.AwaitingApproval,
    &.Booked,
    &.SwaBooked,
    &.Break,
    &.Rejected,
    &.occupied:not(.Break) {
      color: #e2e2e3;
    }

    &.clashed {
      border-top: 1px solid #b21e1d;
      border-bottom: 1px solid #b21e1d;
    
      &:not(.clashed + .clashed) {
        border-left: 1px solid #b21e1d;
      }
      
      &:not(:has(+ .clashed)) {
        border-right: 1px solid #b21e1d;
      }
    }
    
    .clashed + &:not(.clashed) {
      border-left: none;
    }

    .action-AddSlot
      &:not(.occupied):not(.Booked):not(.SwaBooked):not(.Available):not(.AwaitingApproval):not(.Rejected),
    .action-AddSlot .selecting > & {
      cursor: url('/cursor-add.png'), pointer;
    }

    .action-AddBreak &:not(.Booked):not(.SwaBooked):not(.Break),
    .action-AddBreak .selecting > & {
      cursor: url('/cursor-break.png'), pointer;
    }

    .action-AddBreak &.RSS:not(.Booked):not(.Break),
    .action-AddBreak .selecting > &.RSS {
      cursor: not-allowed;
    }

    .action-Clear &.Available:not(.SwaBooked),
    .action-Clear &.AwaitingApproval:not(.SwaBooked),
    .action-Clear &.Break,
    .action-Clear &.Rejected,
    .action-Clear .selecting > & {
      cursor: url('/cursor-clear.png'), pointer;
    }

    &.Available {
      background-color: #21ba45;
    }
    &.AwaitingApproval {
      background-color: #fbbd08;
    }
    &.Booked,
    &.SwaBooked {
      background-color: #bf2020;
    }
    &.Break {
      background-color: #343637;
    }
    &.Rejected {
      color: #909090;
      background-color: #f3f4f5;
      text-decoration: line-through;
      text-decoration-color: #909090;
    }
    &.occupied:not(.Break) {
      background-color: #f3f4f5;
    }
  `,
  Divider: styled.div`
    width: 14px;
    margin: 0;
    border-top: 1px solid #b3b3b3;
    .Available > &,
    .AwaitingApproval > &,
    .Booked > &,
    .SwaBooked > &,
    .Break > &,
    .occupied:not(.Break) > & {
      border-color: #e2e2e3;
    }
    .Rejected > & {
      border-color: #909090;
    }
  `,
};
